import { useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { makeAspectCrop } from 'react-image-crop';

import { PhotoForCropping } from '../../../graphql/queries/albums';

const usePhotoCrop = ({ photoId }) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [loading, setLoading] = useState(true);

  const { data: { photo } = {} } = useQuery(PhotoForCropping, {
    variables: {
      id: photoId,
    },
    skip: !photoId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (photo) {
      setLoading(false);
    }
  }, [photo]);

  const onFaceCenterCrop = ({
    photoToUse = photo,
    container,
    aspectRatio = 1,
    isFullSizeFaceBox = false,
  }) => {
    const { faces } = photoToUse;
    const { naturalWidth: width, naturalHeight: height } = container;
    const { facebox = { x: 0, y: 0, width: 0, height: 0 } } = faces?.records[0];
    let cropBox;

    // No need to crop.
    if (aspectRatio <= 0 || isFullSizeFaceBox) {
      cropBox = {
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0,
      };

      setCrop(cropBox);
      return cropBox;
    }

    const [faceX, faceY, faceWidth, faceHeight] = facebox;
    const pixelWidth = width * faceWidth;
    const pixelHeight = height * faceHeight;
    const isPortraitFace = pixelHeight > pixelWidth;
    const ratio = isPortraitFace
      ? pixelHeight / pixelWidth
      : pixelWidth / pixelHeight;

    const minCropBoxWidth = isPortraitFace ? faceWidth * ratio : faceWidth;
    const minCropBoxHeight = isPortraitFace
      ? faceHeight / aspectRatio
      : (faceHeight * ratio) / aspectRatio;

    let boxMultiplier = 1.75;
    let cropBoxX = faceX - (boxMultiplier * minCropBoxWidth - faceWidth) / 2.0;
    let cropBoxY =
      faceY - (boxMultiplier * minCropBoxHeight - faceHeight) / 2.0;

    cropBox = makeAspectCrop(
      {
        unit: '%',
        width: 100 * boxMultiplier * minCropBoxWidth,
        x: 100 * cropBoxX,
        y: 100 * cropBoxY,
        aspect: aspectRatio,
      },
      width,
      height,
    );

    while (
      boxMultiplier > 1 &&
      (cropBoxX < 0 ||
        cropBoxY < 0 ||
        cropBoxX + cropBox.width > 100 ||
        cropBoxY + cropBox.height > 100)
    ) {
      boxMultiplier = Math.max(1, boxMultiplier - 0.05);
      cropBoxX = faceX - (boxMultiplier * minCropBoxWidth - faceWidth) / 2.0;
      cropBoxY = faceY - (boxMultiplier * minCropBoxHeight - faceHeight) / 2.0;
      cropBox = makeAspectCrop(
        {
          unit: '%',
          width: 100 * boxMultiplier * minCropBoxWidth,
          x: 100 * cropBoxX,
          y: 100 * cropBoxY,
          aspect: aspectRatio,
        },
        width,
        height,
      );
    }

    setCrop(cropBox);

    return cropBox;
  };

  return {
    photo,
    crop,
    setCrop,
    loading,
    onFaceCenterCrop,
  };
};

export default usePhotoCrop;
